// Nastavení: menu vlevo, obsah vpravo (posouvá se zvlášť). Vzhled je v levém panelu vedle jména.
// Sekce: účet, paměť, moje pohledy, zdroje dat, klávesové zkratky, přístupy (audit); správcům navíc Správa (AdminSections).
import { useState, type ReactNode } from 'react';
import {
  BrainIcon,
  ClockCounterClockwiseIcon,
  ClockIcon,
  ChartBarIcon,
  DatabaseIcon,
  DownloadSimpleIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyboardIcon,
  ListChecksIcon,
  ListMagnifyingGlassIcon,
  ShieldCheckIcon,
  SignOutIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import { Badge, Button, EmptyState, IconButton, Input, Modal, Pagination, Spinner, Switch, Textarea, useConfirm, useToast } from '@smworks-cz/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { MEMORY_TYPE_LABEL, ROLE_LABEL, toolLabel, type MeDTO, type MemoryNoteDTO, type MemoryOverviewDTO, type ViewDTO } from '@sam/shared';
import { api } from '../lib/api';
import { keys, queryClient, useAudit, useMemory, useViews } from '../lib/queries';
import { updateView } from '../lib/views';
import { fmtDateTime, fmtMs, fmtParams, reasonText } from '../lib/format';
import { DevUserSwitch } from './Login';
import { isIos, isStandalone, useInstallPrompt } from '../lib/pwa';
import { disablePush, enablePush, pushState } from '../lib/push';
import { AdminAudit, AdminEvents, AdminRules, AdminUsers } from './AdminSections';
import { SchedulesTab } from './Schedules';

interface Props {
  open: boolean;
  onClose: () => void;
  me: MeDTO;
}

type Section = 'ucet' | 'pamet' | 'ulohy' | 'pohledy' | 'zdroje' | 'zkratky' | 'pristupy' | 'adm-users' | 'adm-rules' | 'adm-audit' | 'adm-events';

const SECTIONS: { id: Section; label: string; icon: ReactNode }[] = [
  { id: 'ucet', label: 'Účet', icon: <UserIcon size={16} /> },
  { id: 'pamet', label: 'Paměť', icon: <BrainIcon size={16} /> },
  { id: 'ulohy', label: 'Naplánované úlohy', icon: <ClockIcon size={16} /> },
  { id: 'pohledy', label: 'Moje pohledy', icon: <ChartBarIcon size={16} /> },
  { id: 'zdroje', label: 'Zdroje dat', icon: <DatabaseIcon size={16} /> },
  { id: 'zkratky', label: 'Klávesové zkratky', icon: <KeyboardIcon size={16} /> },
  { id: 'pristupy', label: 'Přístupy', icon: <ListChecksIcon size={16} /> },
];

/** Jen pro správce. */
const ADMIN_SECTIONS: { id: Section; label: string; icon: ReactNode }[] = [
  { id: 'adm-users', label: 'Uživatelé', icon: <UsersIcon size={16} /> },
  { id: 'adm-rules', label: 'Role a pravidla', icon: <ShieldCheckIcon size={16} /> },
  { id: 'adm-audit', label: 'Přístupy všech', icon: <ListMagnifyingGlassIcon size={16} /> },
  { id: 'adm-events', label: 'Historie správy', icon: <ClockCounterClockwiseIcon size={16} /> },
];

export function SettingsModal({ open, onClose, me }: Props) {
  const [tab, setTab] = useState<Section>('ucet');
  const navItem = (x: { id: Section; label: string; icon: ReactNode }) => (
    <button key={x.id} type="button" className={`set-nav-i${tab === x.id ? ' on' : ''}`} aria-current={tab === x.id ? 'page' : undefined} onClick={() => setTab(x.id)}>
      {x.icon}
      <span>{x.label}</span>
    </button>
  );
  const current = [...SECTIONS, ...ADMIN_SECTIONS].find((x) => x.id === tab)!;
  return (
    <Modal open={open} onClose={onClose} title="Nastavení" size="xl" fullscreenBelow="sm">
      <div className="set-layout">
        <nav className="set-nav" aria-label="Sekce nastavení">
          {SECTIONS.map(navItem)}
          {me.user.isAdmin && (
            <>
              <div className="set-nav-h">Správa</div>
              {ADMIN_SECTIONS.map(navItem)}
            </>
          )}
        </nav>
        <div className="set-body" key={tab}>
          <h3 className="set-h">{current.label}</h3>
          {tab === 'ucet' && <AccountTab me={me} />}
          {tab === 'pamet' && <MemoryTab enabled={open} />}
          {tab === 'ulohy' && <SchedulesTab onClose={onClose} />}
          {tab === 'pohledy' && <ViewsTab />}
          {tab === 'zdroje' && <SourcesTab enabled={open} />}
          {tab === 'zkratky' && <ShortcutsTab />}
          {tab === 'pristupy' && <AuditTab enabled={open} />}
          {me.user.isAdmin && tab === 'adm-users' && <AdminUsers me={me} />}
          {me.user.isAdmin && tab === 'adm-rules' && <AdminRules />}
          {me.user.isAdmin && tab === 'adm-audit' && <AdminAudit />}
          {me.user.isAdmin && tab === 'adm-events' && <AdminEvents />}
        </div>
      </div>
    </Modal>
  );
}

function AccountTab({ me }: { me: MeDTO }) {
  const logout = async () => {
    await api('POST', '/auth/logout').catch(() => {});
    window.location.href = '/';
  };
  return (
    <div className="set-list">
      <div className="set-row">
        <div>
          <b>{me.user.name}</b>
          <p className="muted small">
            {me.user.email} · {me.authMode === 'microsoft' ? 'přihlášení přes Microsoft' : 'vývojové přihlášení'}
          </p>
        </div>
        <Button variant="outline" size="sm" icon={<SignOutIcon size={16} />} onClick={() => void logout()}>
          Odhlásit se
        </Button>
      </div>
      <div className="set-row">
        <div>
          <b>Role</b>
          <p className="muted small">Podle role brána rozhoduje, k jakým datům se Sam dostane. Roli mění správce.</p>
        </div>
        <Badge label={ROLE_LABEL[me.user.role]} variant="brand" />
      </div>
      <M365Row />
      <PushRow />
      <InstallRow />
      <div className="set-row">
        <div>
          <b>Vzhled</b>
          <p className="muted small">Přepíná se ikonkou vedle vašeho jména vlevo dole (automaticky, světlý, tmavý).</p>
        </div>
      </div>
      {me.authMode === 'dev' && (
        <div className="set-row">
          <div>
            <b>Testovací uživatel</b>
            <p className="muted small">Jen ve vývojovém režimu. Přepne přihlášeného uživatele.</p>
          </div>
          <DevUserSwitch current={me.user.email} />
        </div>
      )}
    </div>
  );
}

/** Notifikace na tomto zařízení: výsledek naplánované úlohy a odpověď, když se zrovna nedíváte. */
function PushRow() {
  const { toast } = useToast();
  const server = useQuery({ queryKey: ['push'], queryFn: () => api<{ available: boolean; publicKey: string | null; devices: number }>('GET', '/push') });
  const local = useQuery({ queryKey: ['push-state'], queryFn: pushState, staleTime: 0 });
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast({ variant: 'success', title: ok });
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    } finally {
      setBusy(false);
      void local.refetch();
      void server.refetch();
    }
  };
  const st = local.data;
  const text =
    !server.data?.available
      ? 'Notifikace nejsou na serveru nastavené.'
      : st === 'on'
        ? 'Zapnuto na tomto zařízení. Přijde oznámení, když doběhne naplánovaná úloha nebo Sam dopíše odpověď, zatímco se nedíváte. Bez obsahu odpovědi, jen název chatu.'
        : st === 'ios-install'
          ? 'Na iPhonu fungují jen v appce přidané na plochu: v Safari Sdílet → Přidat na plochu, pak appku otevřete z plochy a zapněte je tady.'
          : st === 'denied'
            ? 'Oznámení jsou pro tuto stránku v prohlížeči zakázaná. Povolte je v nastavení prohlížeče (ikona zámku u adresy).'
            : st === 'unsupported'
              ? 'Tento prohlížeč notifikace nepodporuje.'
              : 'Oznámení, když doběhne naplánovaná úloha nebo Sam dopíše odpověď, zatímco se nedíváte. Zapíná se pro každé zařízení zvlášť.';
  return (
    <div className="set-row">
      <div>
        <b>Notifikace na tomto zařízení</b>
        <p className="muted small">{text}</p>
      </div>
      {server.data?.available && st === 'on' && (
        <div className="set-btns">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void run(() => api('POST', '/push/test'), 'Zkušební oznámení odesláno.')}>
            Vyzkoušet
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void run(disablePush)}>
            Vypnout
          </Button>
        </div>
      )}
      {server.data?.available && st === 'off' && (
        <Button size="sm" loading={busy} onClick={() => void run(() => enablePush(server.data!.publicKey!), 'Notifikace zapnuté.')}>
          Zapnout
        </Button>
      )}
    </div>
  );
}

/** Instalace jako aplikace na plochu (PWA). */
function InstallRow() {
  const { canInstall, install } = useInstallPrompt();
  if (isStandalone()) return null;
  return (
    <div className="set-row">
      <div>
        <b>Aplikace na plochu</b>
        <p className="muted small">
          {canInstall
            ? 'Sama si můžete nainstalovat jako aplikaci: vlastní okno a ikona na ploše nebo v telefonu.'
            : isIos()
              ? 'Na iPhonu: v Safari klepněte na Sdílet a pak „Přidat na plochu“.'
              : 'V Chrome nebo Edge: ikona instalace v adresním řádku, na Androidu v menu „Nainstalovat aplikaci“.'}
        </p>
      </div>
      {canInstall && (
        <Button size="sm" onClick={() => void install()}>
          Nainstalovat
        </Button>
      )}
    </div>
  );
}

/** Propojení pošty a souborů z Microsoft 365 (delegovaně, jen čtení). */
function M365Row() {
  const { toast } = useToast();
  const q = useQuery({ queryKey: ['m365'], queryFn: () => api<{ available: boolean; connected: boolean; connectedAt: string | null }>('GET', '/m365') });
  const disconnect = async () => {
    try {
      await api('DELETE', '/m365');
      void q.refetch();
    } catch (e) {
      toast({ variant: 'error', title: (e as Error).message });
    }
  };
  const d = q.data;
  return (
    <div className="set-row">
      <div>
        <b>Microsoft 365: pošta a soubory</b>
        <p className="muted small">
          {d?.connected
            ? `Propojeno ${fmtDateTime(d.connectedAt!)}. Sam může hledat ve vaší poště a v souborech v SharePointu a OneDrivu, jen ke kterým máte přístup, a jen číst.`
            : d && !d.available
              ? 'Propojení teď není k dispozici (jen s přihlášením přes Microsoft a s nastavenou registrací).'
              : 'Po propojení může Sam hledat ve vaší poště a v souborech v SharePointu a OneDrivu, jen ke kterým máte přístup, a jen číst.'}
        </p>
      </div>
      {d?.connected ? (
        <Button variant="outline" size="sm" onClick={() => void disconnect()}>
          Odpojit
        </Button>
      ) : d?.available ? (
        <Button size="sm" href="/auth/m365/connect">
          Propojit
        </Button>
      ) : (
        <Button size="sm" disabled>
          Propojit
        </Button>
      )}
    </div>
  );
}

function ViewsTab() {
  const { data, isLoading } = useViews();
  const confirm = useConfirm();
  const { toast } = useToast();
  const fail = (e: unknown) => toast({ variant: 'error', title: (e as Error).message });
  const remove = async (v: ViewDTO) => {
    const ok = await confirm({ title: 'Odebrat pohled?', description: `Pohled „${v.title}“ zmizí z menu i z přehledu.`, confirmLabel: 'Odebrat', cancelLabel: 'Zrušit', variant: 'danger' });
    if (!ok) return;
    try {
      await api('DELETE', `/views/${v.id}`);
      queryClient.setQueryData<ViewDTO[]>(keys.views, (old) => old?.filter((x) => x.id !== v.id));
    } catch (e) {
      fail(e);
    }
  };
  return (
    <div className="set-list">
      <p className="muted">
        Pohledy jsou grafy a přehledy uložené z chatu. Data se při každém otevření načtou znovu podle vašich práv. Pořadí a velikost karet upravíte přímo na
        úvodní stránce ikonkou vedle nadpisu „Moje pohledy“.
      </p>
      {isLoading && <Spinner />}
      {data && !data.length && <EmptyState size="sm" title="Zatím žádné pohledy" description="Požádejte Sama o graf a u widgetu klikněte na „Uložit jako pohled“." />}
      {data?.map((v) => (
        <div key={v.id} className={`mem view-row${v.onHome ? '' : ' off'}`}>
          <Input
            variant="inline"
            value={v.title}
            onChange={() => {}}
            onCommit={(t) => {
              const title = t.trim();
              if (title && title !== v.title) void updateView(v.id, { title }).catch(fail);
            }}
            aria-label={`Název pohledu ${v.title}`}
          />
          {v.locked && <Badge label="bez oprávnění" variant="neutral" />}
          <IconButton
            icon={v.onHome ? <EyeIcon size={16} /> : <EyeSlashIcon size={16} />}
            label={v.onHome ? 'Skrýt z přehledu' : 'Zobrazit na přehledu'}
            variant="ghost"
            size="sm"
            active={v.onHome}
            onClick={() => void updateView(v.id, { onHome: !v.onHome }).catch(fail)}
          />
          <IconButton icon={<TrashIcon size={16} />} label="Odebrat pohled" variant="ghost" size="sm" onClick={() => void remove(v)} />
        </div>
      ))}
    </div>
  );
}

const SOURCE_INFO: Record<string, { label: string; what: string }> = {
  prdel: { label: 'Prdel', what: 'výkazy, úkoly a projekty z Projektovníku, vytížení, klienti, volno' },
  prepisy: { label: 'Přepisy schůzek', what: 'přepisy nahrávek schůzek s klienty (Sam)' },
  procesy: { label: 'Procesy', what: 'firemní procesy z Toolboxu' },
  smwiki: { label: 'SMwiki', what: 'interní wiki' },
  graph: { label: 'Microsoft 365', what: 'kalendář, kontakty, zasedačky, dostupnost kolegů' },
  toolbox: { label: 'Toolbox', what: 'lobby TV, zápasy' },
  bezvajidlo: { label: 'Jídelní lístek', what: 'Bezva Jídlo' },
};

function SourcesTab({ enabled }: { enabled: boolean }) {
  const q = useQuery({
    queryKey: ['sources'],
    enabled,
    staleTime: 30_000,
    queryFn: async () => (await api<{ sources: { source: string; mode: 'live' | 'mock'; configured: boolean }[] }>('GET', '/sources')).sources,
  });
  return (
    <div className="set-list">
      <p className="muted">
        Odkud Sam bere data. Ostrý zdroj čte skutečná firemní data, testovací vrací vymyšlená data ve stejném tvaru (poznáte je i podle „mock data“ u
        zdroje v odpovědi). K datům se Sam dostane vždy jen přes bránu a podle vaší role.
      </p>
      {q.isLoading && <Spinner />}
      {q.error && <p className="bad">{(q.error as Error).message}</p>}
      {q.data?.map((s) => {
        const info = SOURCE_INFO[s.source] ?? { label: s.source, what: '' };
        const state =
          s.mode === 'mock'
            ? { label: 'testovací data', variant: 'neutral' as const }
            : s.configured
              ? { label: 'ostrý', variant: 'success' as const }
              : { label: 'ostrý, chybí přístup', variant: 'warning' as const };
        return (
          <div key={s.source} className="set-row">
            <div>
              <b>{info.label}</b>
              <p className="muted small">{info.what}</p>
            </div>
            <Badge label={state.label} variant={state.variant} />
          </div>
        );
      })}
    </div>
  );
}

const SHORTCUTS: [string[], string][] = [
  [['Enter'], 'Odeslat zprávu'],
  [['Shift', 'Enter'], 'Nový řádek ve zprávě'],
  [['Ctrl', 'K'], 'Hledat v chatech'],
  [['Ctrl', 'Shift', 'O'], 'Nový chat'],
  [['Ctrl', 'B'], 'Sbalit nebo rozbalit levý panel'],
  [['Ctrl', 'V'], 'Vložit obrázek nebo soubor do zprávy'],
  [['Esc'], 'Zavřít okno, panel přepisu nebo úpravy přehledu'],
];

function ShortcutsTab() {
  const mac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  return (
    <div className="set-list">
      {SHORTCUTS.map(([keysList, what]) => (
        <div key={what} className="set-row">
          <span>{what}</span>
          <span className="kbd-group">
            {keysList.map((k) => (
              <kbd key={k}>{mac && k === 'Ctrl' ? '⌘' : k}</kbd>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

function MemoryTab({ enabled }: { enabled: boolean }) {
  const q = useQuery({ queryKey: ['memory-overview'], enabled, staleTime: 0, queryFn: () => api<MemoryOverviewDTO>('GET', '/memory/overview') });
  const confirm = useConfirm();
  const { toast } = useToast();
  const [profile, setProfile] = useState<string | null>(null);
  const fail = (e: unknown) => toast({ variant: 'error', title: (e as Error).message });
  const refresh = () => void q.refetch();
  const d = q.data;
  const profileValue = profile ?? d?.profile ?? '';

  const del = async (n: MemoryNoteDTO) => {
    try {
      await api('DELETE', `/memory/${n.id}`);
      refresh();
    } catch (e) {
      fail(e);
    }
  };
  const delAll = async () => {
    const ok = await confirm({ title: 'Smazat všechny poznámky?', description: 'Sam zapomene všechny poznámky o vás. Popis a shrnutí chatů zůstanou, smažte je zvlášť, pokud je v nich něco, co si nemá pamatovat.', confirmLabel: 'Smazat vše', cancelLabel: 'Zrušit', variant: 'danger' });
    if (!ok) return;
    await api('DELETE', '/memory');
    queryClient.setQueryData<MemoryNoteDTO[]>(keys.memory, []);
    refresh();
  };
  const toggle = async (on: boolean) => {
    try {
      await api('PATCH', '/memory/settings', { autoMemory: on });
      refresh();
    } catch (e) {
      fail(e);
    }
  };
  const saveProfile = async () => {
    try {
      await api('PUT', '/memory/profile', { profile: profileValue });
      setProfile(null);
      refresh();
      toast({ variant: 'success', title: 'Popis uložen.' });
    } catch (e) {
      fail(e);
    }
  };
  const forget = async (conversationId: string) => {
    try {
      await api('DELETE', `/memory/history/${conversationId}`);
      refresh();
    } catch (e) {
      fail(e);
    }
  };

  return (
    <div className="set-list">
      <p className="muted">
        Sam si o vás vede paměť: popis, poznámky a krátká shrnutí chatů. Na začátku nového chatu je dostane, aby navázal. Vidíte je jen vy a můžete je
        kdykoli upravit nebo smazat. Sazby, mzdy, zdravotní údaje ani hesla se do paměti nikdy neuloží.
      </p>
      {q.isLoading && <Spinner />}
      {q.error && <p className="bad">{(q.error as Error).message}</p>}
      {d && (
        <>
          <div className="set-row">
            <div>
              <b>Automatická paměť</b>
              <p className="muted small">Když chat pár minut utichne, Sam si sám poznamená, co se bude hodit příště, a shrne, o čem chat byl.</p>
            </div>
            <Switch checked={d.autoMemory} onChange={(on) => void toggle(on)} aria-label="Automatická paměť" />
          </div>

          <div className="mem-block">
            <b>Popis</b>
            <Textarea
              value={profileValue}
              onChange={(e) => setProfile(e.target.value)}
              rows={3}
              placeholder="Sam ho doplní sám, nebo ho napište: role, na čem pracujete, jak chcete odpovídat."
              aria-label="Popis uživatele"
            />
            <div className="mem-block-foot">
              <small className="muted">{d.profileUpdatedAt ? `Upraveno ${fmtDateTime(d.profileUpdatedAt)}` : ''}</small>
              {profile !== null && profile !== (d.profile ?? '') && (
                <Button size="sm" onClick={() => void saveProfile()}>
                  Uložit popis
                </Button>
              )}
            </div>
          </div>

          <b className="mem-h">Poznámky</b>
          {!d.notes.length && <EmptyState size="sm" title="Zatím žádné poznámky" description="Sam si je zapíše sám, nebo mu řekněte „zapamatuj si…“." />}
          {d.notes.map((n) => (
            <div key={n.id} className="mem">
              <div>
                {n.text}
                <small>
                  {MEMORY_TYPE_LABEL[n.type]} · {n.source === 'auto' ? 'zapsal Sam sám' : 'na vaše přání'} · {fmtDateTime(n.createdAt)}
                </small>
              </div>
              <IconButton icon={<TrashIcon size={16} />} label="Smazat poznámku" variant="ghost" size="sm" onClick={() => void del(n)} />
            </div>
          ))}

          {d.history.length > 0 && (
            <>
              <b className="mem-h">Historie chatů</b>
              {d.history.map((h) => (
                <div key={h.conversationId} className="mem">
                  <div>
                    <span className="mem-title">{h.title}</span> {h.summary}
                    <small>{fmtDateTime(h.updatedAt)}</small>
                  </div>
                  <IconButton icon={<TrashIcon size={16} />} label="Zapomenout shrnutí" variant="ghost" size="sm" onClick={() => void forget(h.conversationId)} />
                </div>
              ))}
            </>
          )}

          <div className="set-actions">
            <Button variant="outline" size="sm" icon={<DownloadSimpleIcon size={16} />} href="/api/memory/export">
              Exportovat
            </Button>
            {!!d.notes.length && (
              <Button variant="danger-outline" size="sm" onClick={() => void delAll()}>
                Smazat poznámky
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const AUDIT_PAGE = 25;

function AuditTab({ enabled }: { enabled: boolean }) {
  const [page, setPage] = useState(1);
  const q = useAudit(enabled, page, AUDIT_PAGE);
  const { isLoading, error } = q;
  const data = q.data?.entries;
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / AUDIT_PAGE));
  const from = (page - 1) * AUDIT_PAGE + 1;
  return (
    <div className="set-list">
      <p className="muted">
        Každé sáhnutí na data, které Sam udělá vaším jménem, zapíše brána: kdy, jaký nástroj, s jakými parametry, kolik řádků a jestli bylo
        povoleno. Text dotazů a odpovědí se sem neukládá. Vidíte jen své záznamy.
      </p>
      {isLoading && <Spinner />}
      {error && <p className="bad">{(error as Error).message}</p>}
      {data && !data.length && <EmptyState size="sm" title="Zatím žádné přístupy" />}
      {!!data?.length && (
        <div className={`audit${q.isPlaceholderData ? ' is-loading' : ''}`}>
          <table>
            <thead>
              <tr>
                <th>Čas</th>
                <th>Nástroj</th>
                <th>Parametry</th>
                <th>Výsledek</th>
                <th className="r">Řádků</th>
                <th className="r">Doba</th>
              </tr>
            </thead>
            <tbody>
              {data.map((a) => (
                <tr key={a.id}>
                  <td className="nowrap">{fmtDateTime(a.ts)}</td>
                  <td>
                    {toolLabel(a.tool).done}
                    <div className="mono muted">{a.tool}</div>
                  </td>
                  <td className="mono">{fmtParams(a.params)}</td>
                  <td>
                    {a.decision === 'allow' ? <span className="ok">povoleno</span> : <span className="bad">zamítnuto</span>}
                    {a.reason && <div className="muted small">{reasonText(a.reason)}</div>}
                  </td>
                  <td className="r">{a.rows ?? '–'}</td>
                  <td className="r nowrap">{fmtMs(a.ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > AUDIT_PAGE && (
        <div className="audit-pager">
          <span className="muted small">
            {from}–{Math.min(from + AUDIT_PAGE - 1, total)} z {total}
          </span>
          <Pagination page={page} totalPages={pages} onChange={setPage} size="sm" />
        </div>
      )}
    </div>
  );
}
